/**
 * @module app.fm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IDocumentsConfig, IQueueNavigationBinding } from 'app/core/models/essette-interfaces';
import { FaxService, IFaxSearchFilters } from 'app/fm/services/fax-service';
import { apiCall } from 'app/shared/lib/api/api';
import { Toaster } from 'app/shared/lib/toaster';
import { downloadFile, openFileInWindow } from 'app/shared/lib/files';
import { I18N } from 'aurelia-i18n';
import { DocumentsService } from 'app/core/services/documents-service';
import { compareObjects } from 'app/shared/lib/compare-objects';
import * as FM from 'app/fm/models/essette-api';

@autoinject()
export class DocumentQueueNavigation extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private targetId!: string;
    private queueTypeCode!: string;
    private queueId!: string;
    private faxDocumentId!: string;

    private queue: FM.FaxQueue | null = null;
    private faxDocument: FM.FaxDocument | null = null;
    protected nextFax: FM.FaxDocument | null = null;

    protected documentsConfig: IDocumentsConfig | null = null;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly faxService: FaxService,
        private readonly documentsService: DocumentsService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected async activate(params: IQueueNavigationBinding): Promise<void> {
        if(!params.id) { throw new Error('Missing required property: [id]'); }
        if(!params.queueTypeCode) { throw new Error('Missing required property: [queueTypeCode]'); }
        if(!params.queueId) { throw new Error('Missing required property: [queueId]'); }
        if(!params.queueTargetId) { throw new Error('Missing required property: [queueTargetId]'); }
        if(!params.documentsConfig) { throw new Error('Missing required property: [documentsConfig]'); }
        if(params.id === this.targetId && params.queueTypeCode === this.queueTypeCode && this.faxDocumentId === params.queueTargetId
            && compareObjects(params.documentsConfig, this.documentsConfig, {}).areEqual) {
            return;
        }

        this.targetId = params.id;
        this.queueTypeCode = params.queueTypeCode;
        this.queueId = params.queueId;
        this.faxDocumentId = params.queueTargetId;
        this.documentsConfig = params.documentsConfig;

        this.reload();
    }

    protected bind(): void {
        if(this.documentsConfig) this.documentsService.configure(this.documentsConfig);
    }

    protected deactivate(): void { }


    ////****************** Events ***************************************************////

    private async returnToQueue(): Promise<void> {
        this.router.navigateToRoute('fm.queues.fax', { queueId: this.queueId }, { replace: true, trigger: true });
    }

    protected openDocument(): void {
        if(!this.faxDocument) return;

        if(this.shouldView(this.faxDocument?.document?.mediaType?.fileExtension || '')) {
            const childWindow = window.open("", "", 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));
            if(childWindow) {
                this.viewDocument(this.faxDocument, childWindow);
                return;
            }
        }

        this.downloadDocument(this.faxDocument);
    }

    /** Navigates user to an item id, optionally returning auth from it's queue first */
    protected async getNext(nextFaxDocumentId: string | null): Promise<void> {
        if(!!nextFaxDocumentId) {
            this.router.navigate(`#/fm/document/${nextFaxDocumentId}`);

            // Removes focus from button (so it stops flashing after navigating)
            const activeElement: HTMLElement | null = <HTMLElement>document.activeElement;
            if(activeElement && activeElement.blur) { activeElement.blur(); }
        }
        else {
            this.returnToQueue();
        }
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        if(!this.faxDocumentId) { throw new Error('Missing required property: [faxDocumentId]'); }
        if(!this.queueId) { throw new Error('Missing required property: [queueId]'); }

        if(!this.queue || this.queue.id !== this.queueId) {
            this.loadQueue(this.queueId);
        }

        if(!this.faxDocument || this.faxDocumentId !== this.faxDocument.id) {
            await Promise.all([
                this.loadFaxDocument(this.faxDocumentId),
                this.loadNextDocument(this.queueId)
            ]);
        }
    }


    // #region Download/View Document
    protected async downloadDocument(faxDocument: FM.FaxDocument): Promise<void> {
        if(!faxDocument?.document?.id) { return; }

        try {
            const blob = await this.documentsService.downloadFile(faxDocument.document.id);
            const fileName = faxDocument.document?.file?.name || 'download';

            downloadFile(blob, fileName);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
        }
    }

    protected async viewDocument(faxDocument: FM.FaxDocument, childWindow: Window): Promise<void> {
        if(!faxDocument?.document?.id) { return; }

        try {
            const blob = await this.documentsService.downloadFile(faxDocument.document.id);
            openFileInWindow(childWindow, blob);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
            childWindow.close();
        }
    }
    // #endregion Download/View Document

    protected shouldView(extension: string): boolean {
        // TODO: this might warrant being configurable in the future
        const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.txt'];
        const extensionCheck = extension || '';
        return allowed.indexOf(extensionCheck) >= 0;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadQueue(queueId: string): Promise<void> {
        const includes: string[] = []; //['actions', 'actions.page'];

        try {
            const response = await this.faxService.loadQueue(queueId, includes);
            this.queue = response.results[0];
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadFaxDocument(faxDocumentId: string): Promise<void> {
        const includes: string[] = [
            'document',
            'document.mediaType'
        ];
        try {
            this.state.beginLoad();
            const response = await this.faxService.loadFaxDocument(faxDocumentId, includes);
            this.faxDocument = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadNextDocument(queueId: string): Promise<void> {
        try {
            this.state.beginLoad();

            const faxFilters: IFaxSearchFilters = {
                excludeCanceled: true,
                includeUnlocked: true,
                checkoutUserId: this.env.user.id
            };
            const includes: string[] = [];

            const response = await this.faxService.loadFaxesByQueue(queueId, {}, includes, faxFilters);
            // grab the next document checked out by user, or, if none found, the next unlocked document
            this.nextFax = response.results.find(fax => fax.metadata?.locked?.byUser?.id === this.env.user.id) ||
                response.results.find(fax => !fax.isLocked) || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
